<script setup lang="ts">
import type { BottleAccessMode, BottleStatus, LocationInput } from '@wilson/shared'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import LocationField from '../../components/LocationField.vue'
import { api, jsonBody } from '../../services/api'

interface Bottle {
  id: string
  name: string
  status: BottleStatus
  accessMode: BottleAccessMode
  quoteName: string
  hiddenCount: number
  commentCount: number
  lastActivity: string | null
  createdAt: string
}
interface Quote {
  id: string
  name: string
}
const items = ref<Bottle[]>([])
const quotes = ref<Quote[]>([])
const search = ref('')
const status = ref('')
const accessMode = ref('')
const selectedIds = ref<string[]>([])
const showCreate = ref(false)
const error = ref('')
const router = useRouter()
const allVisibleSelected = computed(
  () => items.value.length > 0 && items.value.every(({ id }) => selectedIds.value.includes(id)),
)
const form = ref<{
  name: string
  quoteId: string
  status: 'draft' | 'hidden'
  location?: LocationInput
}>({ name: '', quoteId: '', status: 'draft' })
async function load() {
  const params = new URLSearchParams()
  if (search.value.trim()) params.set('search', search.value.trim())
  if (status.value) params.set('status', status.value)
  if (accessMode.value) params.set('accessMode', accessMode.value)
  try {
    const [bottleData, quoteData] = await Promise.all([
      api<{ items: Bottle[] }>(`/admin/bottles?${params.toString()}`),
      api<{ items: Quote[] }>('/admin/quotes?pageSize=100'),
    ])
    items.value = bottleData.items
    quotes.value = quoteData.items
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Bottles could not be loaded.'
  }
}
async function createBottle() {
  try {
    await api('/admin/bottles', { method: 'POST', ...jsonBody(form.value) })
    showCreate.value = false
    form.value = { name: '', quoteId: '', status: 'draft' }
    await load()
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Bottle could not be created.'
  }
}
function filtersChanged() {
  selectedIds.value = []
  void load()
}
function toggleBottle(id: string, checked: boolean) {
  selectedIds.value = checked
    ? [...new Set([...selectedIds.value, id])]
    : selectedIds.value.filter((selectedId) => selectedId !== id)
}
function toggleVisible(checked: boolean) {
  const visibleIds = items.value.map(({ id }) => id)
  selectedIds.value = checked
    ? [...new Set([...selectedIds.value, ...visibleIds])]
    : selectedIds.value.filter((id) => !visibleIds.includes(id))
}
function openPrint(mode: 'selected' | 'filtered') {
  const query: Record<string, string> = {}
  if (mode === 'selected') {
    if (!selectedIds.value.length) return
    query.ids = selectedIds.value.join(',')
  } else {
    if (search.value.trim()) query.search = search.value.trim()
    if (status.value) query.status = status.value
    if (accessMode.value) query.accessMode = accessMode.value
  }
  window.open(router.resolve({ path: '/admin/bottles/print', query }).href, '_blank', 'noopener')
}
onMounted(load)
</script>

<template>
  <header class="admin-heading">
    <div>
      <p class="eyebrow">Physical travellers</p>
      <h1>Bottles</h1>
      <p>Register each bottle once, print its permanent QR, and preserve every movement.</p>
    </div>
    <button class="button primary" @click="showCreate = !showCreate">+ Register bottle</button>
  </header>
  <p v-if="error" class="form-error banner">{{ error }}</p>
  <form v-if="showCreate" class="admin-card editor-form" @submit.prevent="createBottle">
    <div class="card-heading">
      <h2>Register bottle</h2>
      <button type="button" class="icon-button" @click="showCreate = false">×</button>
    </div>
    <div class="form-grid">
      <label>Name<input v-model="form.name" required maxlength="160" /></label
      ><label
        >Quote<select v-model="form.quoteId" required>
          <option value="" disabled>Select a quote</option>
          <option v-for="quote in quotes" :key="quote.id" :value="quote.id">
            {{ quote.name }}
          </option>
        </select></label
      ><label
        >Status<select v-model="form.status">
          <option value="draft">Draft</option>
          <option value="hidden">Initially hidden</option>
        </select></label
      ><LocationField v-if="form.status === 'hidden'" v-model="form.location" class="span-2" />
    </div>
    <button class="button primary">Register bottle</button>
  </form>
  <section class="admin-card table-card">
    <div class="table-tools">
      <label class="search-field"
        ><span>⌕</span
        ><input v-model="search" placeholder="Search bottles" @input="filtersChanged" /></label
      ><select v-model="status" aria-label="Filter by lifecycle status" @change="filtersChanged">
        <option value="">All statuses</option>
        <option value="draft">Draft</option>
        <option value="hidden">Hidden</option>
        <option value="found">Found</option>
        <option value="missing">Missing</option>
        <option value="retired">Retired</option>
      </select>
      <select v-model="accessMode" aria-label="Filter by access mode" @change="filtersChanged">
        <option value="">All access modes</option>
        <option value="active">Active</option>
        <option value="read_only">Read-only</option>
        <option value="private">Publicly hidden</option>
      </select>
      <div class="button-row print-actions">
        <button
          class="button secondary"
          type="button"
          :disabled="!selectedIds.length"
          @click="openPrint('selected')"
        >
          Print selected ({{ selectedIds.length }})
        </button>
        <button class="button secondary" type="button" @click="openPrint('filtered')">
          Print filtered
        </button>
      </div>
    </div>
    <div class="data-table">
      <table>
        <thead>
          <tr>
            <th class="selection-cell">
              <label>
                <span class="sr-only">Select all visible bottles</span>
                <input
                  type="checkbox"
                  :checked="allVisibleSelected"
                  :indeterminate="selectedIds.length > 0 && !allVisibleSelected"
                  @change="toggleVisible(($event.target as HTMLInputElement).checked)"
                />
              </label>
            </th>
            <th>Bottle</th>
            <th>Quote</th>
            <th>Status</th>
            <th>Access</th>
            <th>Hidden</th>
            <th>Notes</th>
            <th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="bottle in items" :key="bottle.id">
            <td class="selection-cell">
              <label>
                <span class="sr-only">Select {{ bottle.name }}</span>
                <input
                  type="checkbox"
                  :checked="selectedIds.includes(bottle.id)"
                  @change="toggleBottle(bottle.id, ($event.target as HTMLInputElement).checked)"
                />
              </label>
            </td>
            <td>
              <RouterLink :to="`/admin/bottles/${bottle.id}`"
                ><strong>{{ bottle.name }}</strong
                ><small>{{ bottle.id }}</small></RouterLink
              >
            </td>
            <td>{{ bottle.quoteName }}</td>
            <td>
              <span class="status-pill" :class="bottle.status">{{ bottle.status }}</span>
            </td>
            <td>
              <span class="status-pill" :class="bottle.accessMode">{{
                bottle.accessMode.replace('_', ' ')
              }}</span>
            </td>
            <td>{{ bottle.hiddenCount }}</td>
            <td>{{ bottle.commentCount }}</td>
            <td>
              {{ bottle.lastActivity ? new Date(bottle.lastActivity).toLocaleDateString() : '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
